<script setup lang="ts">
import { ref, computed } from 'vue'
import { IDegree } from '@shared/types'
import { formatDate } from '@/utils/dateFormatter'
const props = defineProps<{
	degree: IDegree
}>()

const isExpanded = ref(false)

const hasAdditionalDetails = computed(
	() => Boolean(props.degree.cadenaOriginal) || Boolean(props.degree.xml) || Boolean(props.degree.data.numeroControl) || Boolean(props.degree.data.Antecedente) || Boolean(props.degree.data.Expedicion)
)

const getErrorMessage = (error: any) => {
	if (typeof error === 'string') {
		return error.length > 63 ? error.substring(0, 63) + '...' : error
	}
	if (error && typeof error === 'object' && typeof error.message === 'string') {
		const message: string = error.message
		if (message.split(':').length > 1) {
			return message.split(':')[0].trim()
		} else if (message.length > 90) {
			return message.substring(0, 90) + '...'
		} else {
			return message
		}
	}
	return 'Error desconocido'
}

const toggleDetails = () => {
	isExpanded.value = !isExpanded.value
}
</script>

<template>
	<div class="degree-content">
		<!-- Información Principal del Profesionista -->
		<div class="mb-6">
			<div class="section-title">
				<VaIcon name="person" class="title-icon" />
				Información del Profesionista
			</div>
			<div class="student-card">
				<div class="gap-4 mb-4">
					<div class="student-avatar">
						<VaIcon name="person" class="student-icon" />
					</div>
					<div class="flex-1">
						<h3 class="student-name">{{ props.degree.data.Profesionista.nombre }} {{ props.degree.data.Profesionista.primerApellido }} {{ props.degree.data.Profesionista.segundoApellido }}</h3>
						<p class="student-career">{{ props.degree.data.Carrera.nombreCarrera }}</p>
					</div>
				</div>

				<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div class="info-item">
						<VaIcon name="fingerprint" class="info-icon" />
						<span class="info-label">CURP:</span>
						<span class="info-value">{{ props.degree.data.Profesionista.curp }}</span>
					</div>
					<div class="info-item">
						<VaIcon name="email" class="info-icon" />
						<span class="info-label">Email:</span>
						<span class="info-value">{{ props.degree.data.Profesionista.correoElectronico }}</span>
					</div>
					<div class="info-item">
						<VaIcon name="badge" class="info-icon" />
						<span class="info-label">Número de Control:</span>
						<span class="info-value">{{ props.degree.data.numeroControl }}</span>
					</div>
					<div class="info-item">
						<VaIcon name="school" class="info-icon" />
						<span class="info-label">Modalidad:</span>
						<span class="info-value">{{ props.degree.data.Expedicion.modalidadTitulacion }}</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Alertas y Errores -->
		<div v-if="props.degree.warning || props.degree.error" class="mb-6">
			<div class="section-title">
				<VaIcon name="warning" class="title-icon" />
				Alertas y Errores
			</div>
			<div class="package-card">
				<div class="alert-item" :class="props.degree.error ? 'alert-error' : 'alert-warning'">
					<VaIcon name="warning" class="alert-icon" />
					<span class="alert-message">{{ getErrorMessage(props.degree.error || props.degree.warning) }}</span>
				</div>
			</div>
		</div>

		<!-- Botón para expandir detalles -->
		<div v-if="hasAdditionalDetails" class="text-center mb-4">
			<button @click="toggleDetails" class="btn btn-primary flex items-center gap-2 mx-auto">
				<VaIcon :name="isExpanded ? 'expand_less' : 'expand_more'" />
				{{ isExpanded ? 'Ocultar detalles' : 'Ver más detalles' }}
			</button>
		</div>

		<!-- Detalles Expandidos -->
		<div v-if="isExpanded" class="space-y-6">
			<!-- Información de Expedición -->
			<div class="mb-6">
				<div class="section-title">
					<VaIcon name="event" class="title-icon" />
					Información de Expedición
				</div>
				<div class="package-card">
					<div class="grid grid-cols-1 md:grid-cols-3 gap-4">
						<div class="info-item">
							<VaIcon name="fingerprint" class="info-icon" />
							<span class="info-label">Folio Control:</span>
							<span class="info-value">{{ props.degree.folioControl || 'No disponible' }}</span>
						</div>
						<div class="info-item">
							<VaIcon name="event" class="info-icon" />
							<span class="info-label">Fecha Expedición:</span>
							<span class="info-value">{{ formatDate(props.degree.data.Expedicion.fechaExpedicion) }}</span>
						</div>
						<div class="info-item">
							<VaIcon name="business" class="info-icon" />
							<span class="info-label">Institución:</span>
							<span class="info-value">{{ props.degree.data.Institucion.nombreInstitucion }}</span>
						</div>
					</div>
				</div>
			</div>
			<!-- Antecedentes Académicos -->
			<div>
				<div class="section-title">
					<VaIcon name="history_edu" class="title-icon" />
					Antecedentes Académicos
				</div>
				<div class="package-card">
					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div class="info-item">
							<VaIcon name="school" class="info-icon" />
							<span class="info-label">Institución:</span>
							<span class="info-value">{{ props.degree.data.Antecedente.institucionProcedencia }}</span>
						</div>
						<div class="info-item">
							<VaIcon name="book" class="info-icon" />
							<span class="info-label">Tipo de Estudio:</span>
							<span class="info-value">{{ props.degree.data.Antecedente.tipoEstudioAntecedente }}</span>
						</div>
						<div class="info-item">
							<VaIcon name="location_on" class="info-icon" />
							<span class="info-label">Entidad Federativa:</span>
							<span class="info-value">{{ props.degree.data.Antecedente.entidadFederativa }}</span>
						</div>
						<div class="info-item">
							<VaIcon name="event" class="info-icon" />
							<span class="info-label">Fecha de Terminación:</span>
							<span class="info-value">{{ formatDate(props.degree.data.Antecedente.fechaTerminacion) }}</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Información de Expedición Detallada -->
			<div>
				<div class="section-title">
					<VaIcon name="assignment" class="title-icon" />
					Información de Expedición Detallada
				</div>
				<div class="package-card">
					<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
						<div class="info-item">
							<VaIcon name="volunteer_activism" class="info-icon" />
							<span class="info-label">Servicio Social:</span>
							<span class="info-value">{{ props.degree.data.Expedicion.cumplioServicioSocial ? 'Cumplido' : 'No cumplido' }}</span>
						</div>
						<div class="info-item">
							<VaIcon name="gavel" class="info-icon" />
							<span class="info-label">Fundamento Legal SS:</span>
							<span class="info-value">{{ props.degree.data.Expedicion.fundamentoLegalServicioSocial }}</span>
						</div>
						<div v-if="props.degree.data.Expedicion.fechaExencionExamenProfesional" class="info-item">
							<VaIcon name="event" class="info-icon" />
							<span class="info-label">Fecha Exención:</span>
							<span class="info-value">{{ formatDate(props.degree.data.Expedicion.fechaExencionExamenProfesional) }}</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Información Técnica -->
			<div v-if="props.degree.cadenaOriginal">
				<div class="section-title">
					<VaIcon name="code" class="title-icon" />
					Información Técnica
				</div>
				<div class="package-card">
					<div class="info-item">
						<VaIcon name="link" class="info-icon" />
						<span class="info-label">Cadena Original:</span>
					</div>
					<div class="mt-3 p-4 bg-gray-50 rounded-lg border">
						<code class="text-sm text-gray-700 break-all">{{ props.degree.cadenaOriginal }}</code>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
/* Títulos de sección */
.section-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 1.5rem;
	padding-bottom: 0.75rem;
	border-bottom: 2px solid #e9ecef;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.title-icon {
	color: #007bff;
	font-size: 1.25rem;
}

/* Tarjetas base */
.student-card,
.package-card {
	background: white;
	border-radius: 12px;
	border: 2px solid #e9ecef;
	padding: 1.25rem;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	animation: fadeIn 0.5s ease-in-out;
}

.student-card:hover,
.package-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

/* Avatares */
.student-avatar {
	width: 25px;
	height: 25px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6ab0ff 0%, #0873c4 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.student-icon {
	color: white;
	font-size: 1rem;
}

/* Información del estudiante */
.student-name {
	font-size: 1.25rem;
	font-weight: 600;
	margin: 0 0 0.25rem 0;
	color: #1a2a3a;
}

.student-career {
	font-size: 0.875rem;
	margin: 0;
	color: #6c757d;
}

/* Elementos de información */
.info-item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.75rem;
	background: #f8f9fa;
	border-radius: 8px;
	border-left: 4px solid #007bff;
}

.info-icon {
	color: #007bff;
	font-size: 1.125rem;
	flex-shrink: 0;
}

.info-label {
	font-weight: 600;
	color: #1a2a3a;
	min-width: 120px;
}

.info-value {
	color: #6c757d;
	flex: 1;
}

/* Alertas */
.alert-item {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 1rem;
	border-radius: 8px;
	border-left: 4px solid;
}

.alert-error {
	background: #fef2f2;
	border-left-color: #dc2626;
}

.alert-warning {
	background: #fffbeb;
	border-left-color: #f59e0b;
}

.alert-icon {
	font-size: 1.25rem;
	flex-shrink: 0;
}

.alert-error .alert-icon {
	color: #dc2626;
}

.alert-warning .alert-icon {
	color: #f59e0b;
}

.alert-message {
	font-weight: 600;
	flex: 1;
}

.alert-error .alert-message {
	color: #dc2626;
}

.alert-warning .alert-message {
	color: #f59e0b;
}

/* Botones */
.btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.75rem 1rem;
	border-radius: 8px;
	font-weight: 600;
	font-size: 0.875rem;
	transition: all 0.3s ease;
	border: none;
	cursor: pointer;
	text-decoration: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn-primary {
	background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
	color: white;
}

.btn-primary:hover {
	background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
	transform: translateY(-1px);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive design */
@media (max-width: 768px) {
	.info-item {
		flex-direction: column;
		align-items: flex-start;
		gap: 0.5rem;
	}

	.info-label {
		min-width: auto;
	}

	.section-title {
		font-size: 1.125rem;
	}

	.student-avatar {
		width: 40px;
		height: 40px;
	}

	.student-icon {
		font-size: 1rem;
	}
}

/* Utilidades */
.space-y-6 > * + * {
	margin-top: 1.5rem;
}

.mb-4 {
	margin-bottom: 1rem;
}

.mb-6 {
	margin-bottom: 1.5rem;
}

.gap-2 {
	gap: 0.5rem;
}

.gap-4 {
	gap: 1rem;
}

.flex {
	display: flex;
}

.items-center {
	align-items: center;
}

.text-center {
	text-align: center;
}

.text-gray-700 {
	color: #374151;
}

.text-sm {
	font-size: 0.875rem;
}

.break-all {
	word-break: break-all;
}

.bg-gray-50 {
	background-color: #f9fafb;
}

.rounded-lg {
	border-radius: 0.5rem;
}

.border {
	border: 1px solid #e5e7eb;
}

.mt-3 {
	margin-top: 0.75rem;
}

.p-4 {
	padding: 1rem;
}
</style>
